import type { HomeStateResponse } from "@townsy/contracts";
import { isApiError } from "../api/client";

/**
 * Coin purchases at Townsy Home Builders / Furnishings (item buys, home upgrades) and the free
 * layout/look/open-closed changes (docs/04 sections 6 and 9, HOMES_ROUND2_BRIEF).
 *
 * Purchases follow the plot purchase exactly: one Idempotency-Key per confirmation, reused verbatim
 * on every retry of that confirmation (even after the dialog was closed and reopened while the
 * outcome was unknown), so a lost response can never spend twice. A different item, level or
 * price is a different attempt with a new key.
 *
 * Layout changes carry `expectedLayoutVersion`: a stale or repeated request is refused with 409
 * and the client refetches instead of overwriting someone else's change.
 */

export function makeIdempotencyKey(prefix: string): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

export interface CommandFailure {
  ok: false;
  code: string;
  message: string;
  /** Safe to resend with the SAME key (network failure, 5xx, rate limit). */
  retryable: boolean;
  /** Prices/home/balances are stale: refresh and start a NEW attempt (new key, new body). */
  refresh: boolean;
}

export type CommandOutcome<R> = { ok: true; response: R } | CommandFailure;

export type CommandKind = "item" | "upgrade";

/** One confirmation of one body for one target (item id or property id). */
export class CommandAttempt<B, R> {
  readonly key: string;
  private tries = 0;
  private inFlight: Promise<CommandOutcome<R>> | null = null;
  private done: CommandOutcome<R> | null = null;

  constructor(
    readonly kind: CommandKind,
    readonly target: string,
    readonly body: B,
    makeKey: () => string,
  ) {
    this.key = makeKey();
  }

  get attempts(): number {
    return this.tries;
  }

  get settled(): CommandOutcome<R> | null {
    return this.done;
  }

  /** Send (or resend) this attempt. Concurrent calls share one request; a success is final. */
  submit(send: (target: string, body: B, idempotencyKey: string) => Promise<R>): Promise<CommandOutcome<R>> {
    if (this.done?.ok) return Promise.resolve(this.done);
    if (this.inFlight) return this.inFlight;
    this.tries++;
    const run = (async (): Promise<CommandOutcome<R>> => {
      try {
        return { ok: true, response: await send(this.target, this.body, this.key) };
      } catch (err) {
        return describeCommandError(err, this.kind);
      }
    })();
    this.inFlight = run;
    return run.then((outcome) => {
      this.inFlight = null;
      this.done = outcome;
      return outcome;
    });
  }
}

/**
 * Keeps an UNRESOLVED attempt per (kind, target, body) so reopening the review resends the
 * original key; resolved attempts are forgotten (buying a second bush is a new attempt).
 */
export class CommandAttemptRegistry {
  private readonly open = new Map<string, CommandAttempt<unknown, unknown>>();

  constructor(private readonly makeKey: (kind: CommandKind) => string = (kind) => makeIdempotencyKey(kind === "item" ? "item" : "upgrade")) {}

  private id(kind: CommandKind, target: string, body: unknown): string {
    return `${kind}:${target}:${JSON.stringify(body)}`;
  }

  forTarget<B, R>(kind: CommandKind, target: string, body: B): CommandAttempt<B, R> {
    const id = this.id(kind, target, body);
    let attempt = this.open.get(id) as CommandAttempt<B, R> | undefined;
    if (!attempt) {
      attempt = new CommandAttempt<B, R>(kind, target, body, () => this.makeKey(kind));
      this.open.set(id, attempt as CommandAttempt<unknown, unknown>);
    }
    return attempt;
  }

  settle(attempt: CommandAttempt<unknown, unknown>): void {
    const id = this.id(attempt.kind, attempt.target, attempt.body);
    if (this.open.get(id) === attempt) this.open.delete(id);
  }

  get size(): number {
    return this.open.size;
  }
}

/** One registry per page load (the server keeps the authoritative record). */
export const homeCommandAttempts = new CommandAttemptRegistry();

/** Every error code the item purchase / upgrade endpoints can return, in plain language. */
export function describeCommandError(err: unknown, kind: CommandKind): CommandFailure {
  const what = kind === "item" ? "purchase" : "upgrade";
  if (!isApiError(err)) {
    return {
      ok: false,
      code: "NETWORK",
      message: `Couldn't reach Townsy to confirm the ${what}. Try again: a repeated attempt can never charge twice.`,
      retryable: true,
      refresh: false,
    };
  }
  const fail = (message: string, opts: { retryable?: boolean; refresh?: boolean } = {}): CommandFailure => ({
    ok: false,
    code: err.code,
    message,
    retryable: opts.retryable ?? false,
    refresh: opts.refresh ?? false,
  });
  switch (err.code) {
    case "UNAUTHENTICATED":
      return fail(kind === "item" ? "Sign in to buy items. Your session may have expired." : "Sign in to upgrade your home. Your session may have expired.");
    case "EMAIL_NOT_VERIFIED":
      return fail(kind === "item" ? "Verify your email address before buying items." : "Verify your email address before upgrading your home.");
    case "FEATURE_DISABLED":
      return fail("Home upgrades and items are switched off right now.");
    case "HOME_REQUIRED":
      return fail("Buy a plot first at Townsy Real Estate, 1 Plaza Way. Nothing was spent.", { refresh: true });
    case "STAGE_NOT_NEXT":
      return fail("Your home is no longer at the level you saw (it may already be upgraded). Nothing extra was spent — check the next level and confirm again.", {
        refresh: true,
      });
    case "PRICE_CHANGED":
      return fail("The price changed since you opened this. Nothing was spent — review the new price and confirm again.", { refresh: true });
    case "INSUFFICIENT_COINS":
      return fail("You don't have enough coins for this. Nothing was spent.", { refresh: true });
    case "IDEMPOTENCY_CONFLICT":
      return fail("This confirmation clashed with an earlier request. Nothing extra was spent — please start again.", { refresh: true });
    case "PROPERTY_TYPE_NOT_ELIGIBLE":
      return fail("Only homes on residential plots can be upgraded.");
    case "RATE_LIMITED":
      return fail("Too many attempts. Wait a moment, then try again.", { retryable: true });
    case "NOT_FOUND":
      return fail(kind === "item" ? "This item is no longer sold." : "This home could not be found.", { refresh: true });
    case "FORBIDDEN":
    case "BANNED":
      return fail(kind === "item" ? "Your account can't buy items right now." : "Only the owner can upgrade this home.");
    case "VALIDATION_FAILED":
      return fail(`The request was rejected: ${err.message || "invalid request"}.`, { refresh: true });
    case "DEPENDENCY_UNAVAILABLE":
    case "INTERNAL":
      return fail(`Townsy couldn't confirm the ${what}. Try again: a repeated attempt can never charge twice.`, { retryable: true });
    default:
      return fail(err.message || `The ${what} could not be completed.`, { retryable: err.retryable });
  }
}

// ---------------------------------------------------------------- layout / look / open-closed

export type LayoutOutcome =
  | { ok: true; state: HomeStateResponse }
  | {
      ok: false;
      code: string;
      message: string;
      /** The home changed elsewhere (409): the caller refetched it; the visitor reviews and tries again. */
      conflict: boolean;
    };

export function describeLayoutError(err: unknown): Extract<LayoutOutcome, { ok: false }> {
  if (!isApiError(err)) {
    return { ok: false, code: "NETWORK", message: "Couldn't reach Townsy. Nothing changed — try again.", conflict: false };
  }
  const fail = (message: string, conflict = false) => ({ ok: false as const, code: err.code, message, conflict });
  switch (err.code) {
    case "PROPERTY_VERSION_CHANGED":
      return fail("Your home was changed somewhere else (another tab or device). We loaded the latest version — check it and try again.", true);
    case "ANCHOR_OCCUPIED":
      return fail("That spot is already taken. We loaded the latest layout — pick another spot.", true);
    case "VALIDATION_FAILED":
      return fail(`That doesn't fit there: ${err.message || "choose another spot or rotation"}.`, true);
    case "NOT_FOUND":
      return fail("That item or spot no longer exists. We loaded the latest layout.", true);
    case "UNAUTHENTICATED":
      return fail("Sign in again to change your home.");
    case "FORBIDDEN":
    case "BANNED":
      return fail("Only the owner can change this home.");
    case "EMAIL_NOT_VERIFIED":
      return fail("Verify your email address to change your home.");
    case "FEATURE_DISABLED":
      return fail("Home changes are switched off right now.");
    case "RATE_LIMITED":
      return fail("Too many changes at once. Wait a moment, then try again.");
    default:
      return fail(err.retryable || err.status >= 500 ? "Townsy couldn't save that change. Nothing changed — try again." : err.message || "That change was not accepted.");
  }
}

/**
 * Run one versioned layout/look/open-closed change. On a 409 (or a refusal that means our copy of the
 * home is stale) `onConflict` refetches before the visitor sees the message, so the next attempt
 * uses the fresh `layoutVersion`. Never retried automatically: a change is a deliberate choice.
 */
export async function runLayoutChange(
  send: () => Promise<HomeStateResponse>,
  hooks: { onConflict(): Promise<unknown> | void },
): Promise<LayoutOutcome> {
  try {
    return { ok: true, state: await send() };
  } catch (err) {
    const outcome = describeLayoutError(err);
    if (outcome.conflict) {
      try {
        await hooks.onConflict();
      } catch {
        /* the refetch error surfaces through the queries */
      }
    }
    return outcome;
  }
}
